// SPDX-License-Identifier: MIT
// Generated by scripts/browser.ts from ../reel.ts. Do not edit.
/**
 * `reel.json` — the complete description of a reel's picture (§7.5.3).
 * M0 subset: structure, timing and scene list. Fact-reference checks arrive with brand.json in M1.
 */
import * as z from 'zod/mini';
export const REEL_SCHEMA_ID = 'pulsereel.reel/1';
const hex = /*@__PURE__*/ (() => z.string().check(z.regex(/^#[0-9a-fA-F]{6}$/, 'must be #RRGGBB')))();
export const SceneEntrySchema = /*@__PURE__*/ (() =>
  z.object({
    id: z.string().check(z.regex(/^[a-z0-9][a-z0-9-]{0,31}$/)),
    /** Scene-kit type, or `custom:<name>` for an agent-written scene (§7.7). */
    type: z.string().check(z.regex(/^(custom:)?[a-z][a-z0-9-]{0,47}$/)),
    /** Half-open beat range [start, end). */
    beats: z.tuple([z.number().check(z.int()).check(z.gte(0)), z.number().check(z.int()).check(z.gte(1))]),
    props: z._default(z.record(z.string(), z.unknown()), {}),
    /**
     * Ground for this scene when it differs from the style's: a dark beat inside a light film, or a
     * daylight beat inside a dark one. Scenes built on `kit.theme` follow it.
     */
    ground: z.optional(z.enum(['dark', 'light'])),
    /** Transition into the next scene. */
    out: z.optional(z.string()),
    src: z.optional(z.string()),
  }))();
export const CueSchema = /*@__PURE__*/ (() =>
  z.object({
    beat: z.number().check(z.gte(0)),
    sfx: z.optional(z.string()),
    /** Visual accent: drives flash, aberration and shake on the beat. Default: 0.6 with an sfx, else 0. */
    accent: z.optional(z.number().check(z.gte(0)).check(z.lte(1))),
    gain: z.optional(z.number().check(z.gte(0)).check(z.lte(2))),
  }))();
export const ReelSchema = /*@__PURE__*/ (() =>
  z
    .object({
      schema: z.literal(REEL_SCHEMA_ID),
      engine: z.string(),
      size: z.tuple([
        z.number().check(z.int()).check(z.gte(16)).check(z.lte(7680)),
        z.number().check(z.int()).check(z.gte(16)).check(z.lte(7680)),
      ]),
      fps: z.number().check(z.int()).check(z.gte(1)).check(z.lte(120)),
      bpm: z.number().check(z.gte(40)).check(z.lte(240)),
      beats: z.number().check(z.int()).check(z.gte(1)).check(z.lte(512)),
      style: z.string(),
      music: z.optional(
        z.object({
          preset: z.string(),
          key: z.optional(z.string()),
          progression: z.optional(z.array(z.string())),
        }),
      ),
      brandRef: z.optional(z.string()),
      palette: z.optional(z.record(z.string(), hex)),
      scenes: z.array(SceneEntrySchema).check(z.minLength(1)),
      cues: z._default(z.array(CueSchema), []),
      seed: z.number().check(z.int()).check(z.gte(0)).check(z.lte(0xffffffff)),
    })
    .check(
      z.superRefine((reel, ctx) => {
        // Scenes must tile [0, beats) exactly: contiguous, non-overlapping, in order.
        let cursor = 0;
        reel.scenes.forEach((scene, i) => {
          const [start, end] = scene.beats;
          if (end <= start)
            ctx.addIssue({
              code: 'custom',
              path: ['scenes', i, 'beats'],
              message: 'end must be after start',
            });
          if (start !== cursor)
            ctx.addIssue({
              code: 'custom',
              path: ['scenes', i, 'beats'],
              message: `expected scene to start at beat ${cursor}, got ${start}`,
            });
          cursor = end;
        });
        if (cursor !== reel.beats)
          ctx.addIssue({
            code: 'custom',
            path: ['scenes'],
            message: `scenes cover ${cursor} of ${reel.beats} beats`,
          });
        const ids = new Set<string>();
        reel.scenes.forEach((scene, i) => {
          if (ids.has(scene.id))
            ctx.addIssue({
              code: 'custom',
              path: ['scenes', i, 'id'],
              message: `duplicate scene id ${scene.id}`,
            });
          ids.add(scene.id);
        });
        reel.cues.forEach((cue, i) => {
          if (cue.beat > reel.beats)
            ctx.addIssue({
              code: 'custom',
              path: ['cues', i, 'beat'],
              message: 'cue is past the end of the reel',
            });
        });
      }),
    ))();
export type Reel = z.infer<typeof ReelSchema>;
export type SceneEntry = z.infer<typeof SceneEntrySchema>;
export type Cue = z.infer<typeof CueSchema>;
export function reelDuration(reel: Pick<Reel, 'beats' | 'bpm'>): number {
  return (reel.beats * 60) / reel.bpm;
}
export function reelFrameCount(reel: Pick<Reel, 'beats' | 'bpm' | 'fps'>, fps = reel.fps): number {
  return Math.round(reelDuration(reel) * fps);
}
